<template>
    <div ref='screen-container' class='screen-container'>
      <div ref='screen' class='screen'>
          <span class='title title-initial glowText'>Super Video Ball</span>
          <span ref='scoreboard' class='score-board'>:</span>
          <div ref='game-container' class='game-container'>
            <div ref='phaser-container' id='phaser-container'></div>
            <div ref='game-message' class='game-message glowText'></div>
          </div>
          <span ref='players' class='score-board'>
                <span>{{player1.chosenName}}</span>
                <span> VS </span>
                <span>{{player2.chosenName}}</span> 
          </span>
      </div>
    </div>
</template>

<script>
import Paddle from './../svb/Paddle'
import Ball from './../svb/Ball'
import BotAI from './../svb/BotAI'
import { applyScoreEffects, applyPaddleHitEffects, applyRallyHeatEffects } from './../svb/effects'

import onepx_png from './../svb/assets/1px.png'
import ball_png from './../svb/assets/ball.png'
import paddle_png from './../svb/assets/paddle.png'

export default {

  props: {
    network: Object,
    player1: {
      type: Object,
      default: () => { return {} },
    },
    player2: {
      type: Object,
      default: () => { return {} },
    },
    cpu1: {
      type: Boolean,
      default () { return false },
    },
    cpu2: {
      type: Boolean,
      default () { return false },
    },
    cpu1Profile: {
      type: String,
      default () { return 'normal' },
    },
    cpu2Profile: {
      type: String,
      default () { return 'normal' },
    },
    onGameReady: {
      type: Function,
      default () { () => {} },
    },
    onMatchFinished: {
      type: Function,
      default: () => (() => {}),
    },
    targetScore: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      element: null,
      scoreBoard: null,
      gameContainer: null,

      game: null,
      sound: null,

      paddle1: null,
      paddle2: null,
      ball: null,

      score: null,

      cpu1data: {},
      cpu2data: {},
      bot1: null,
      bot2: null,
      gameReadyTimeoutId: null,
      matchResetTimeoutId: null,
    }
  },

  mounted() {
    this.score = [0, 0]
    this.setupBots()

    this.createGame()
  },

  methods: {

    getRef(name) {
      if (!this.$refs) {
        return null
      }
      return this.$refs[name] || null
    },

    clearScheduledTasks() {
      if (this.gameReadyTimeoutId) {
        clearTimeout(this.gameReadyTimeoutId)
        this.gameReadyTimeoutId = null
      }
      if (this.matchResetTimeoutId) {
        clearTimeout(this.matchResetTimeoutId)
        this.matchResetTimeoutId = null
      }
    },

    terminate() {
      this.clearScheduledTasks()
      if (this.game && typeof this.game.destroy === 'function') {
        this.game.destroy(true, false)
      }
      this.game = null
    },

    checkDimensions() {
      // debugger
      const screenContainer = this.getRef('screen-container')
      const screen = this.getRef('screen')
      if (!screenContainer || !screen) {
        return
      }
      const width = screen.offsetWidth
      const height = screen.offsetHeight
      const containerWidth = screenContainer.offsetWidth
      const containerHeight = screenContainer.offsetHeight

      let transform = '';

      // center screen in screen-container
      transform += `translate(${containerWidth/2-width/2}px,${containerHeight/2 - height/2}px)`
      
      // determine the ratio of the content
      const ratio = width / height
      const containerRatio = containerWidth / containerHeight
      
      // width restricted
      if (ratio > containerRatio) {

        const factor = containerWidth / width
        transform += `scale(${factor})`
      }
      // height restricted
      else {
        const factor = containerHeight / height
        transform += `scale(${factor})`
      }

      // set
      screen.style.transform = transform;
    },

    createGame() {

      // window.addEventListener('resize', this.checkDimensions)
      // window.addEventListener('orientationchange', this.checkDimensions)

      this.game = new window.Phaser.Game({
        forceSetTimeOut: false,
        width: 700, height: 400, rederer: window.Phaser.WEBGL, parent: 'phaser-container', state: {

        preload: () => {
          // Prevent the game from sleeping if focus is lost
          this.game.stage.disableVisibilityChange = true

          // Load stuff
          this.game.load.image('wall', onepx_png)
          this.game.load.image('paddle', paddle_png)
          this.game.load.image('ball', ball_png)

          this.game.load.script('BlurX', '/lib/filters/blurx.js')
          this.game.load.script('BlurY', '/lib/filters/blury.js')
          this.game.load.script('BallBlur', '/lib/filters/ballblur.js')

          this.game.load.audio('sfx', '/sound.mp3')
        },

        create: (game) => {
          // console.log('Screen.createGame.Phaser.create', game)

          //	Set up audo sprite  
          this.sound = game.add.audio('sfx')
          this.sound.allowMultiple = true
          this.sound.addMarker('ball', 0, 1.0)
          this.sound.addMarker('paddle left', 2, 1.0)
          this.sound.addMarker('paddle right', 4, 1.0)
          this.sound.addMarker('default', 6, 1.0)

          // Setup scaling
          this.game.scale.scaleMode = window.Phaser.ScaleManager.NO_SCALE
          this.game.scale.refresh()

          // Setup physics
          game.physics.startSystem(window.Phaser.Physics.ARCADE)

          // Setup paddles
          this.paddle1 = new Paddle(this.game, 'left', this.sound)
          this.paddle2 = new Paddle(this.game, 'right', this.sound)

          // Setup ball
          this.ball = new Ball(this.game, this.ballScored, this.sound)

          // Prevent bouncing left and right
          game.physics.arcade.checkCollision.left = false
          game.physics.arcade.checkCollision.right = false

          // Display the game
          const gameContainer = this.getRef('game-container')
          if (gameContainer) {
            gameContainer.classList.add('ready')
          }

          // Setup callbacks
          if(this.network) {
            this.setupCallbacks()
          }

          // Scale screen
          this.checkDimensions()

          // Start the game
          this.updateScore()

          // Got to wait a bit
          this.gameReadyTimeoutId = setTimeout(() => {
            this.onGameReady()
            this.gameReadyTimeoutId = null
          }, 500)
        },

        update: (game) => {
          // console.log('Screen.createGame.Phaser.update', game)
          if (this.cpu1) {
            this.updateBot(0)
          }

          if (this.cpu2) {
            this.updateBot(1)
          }

          // Update ball
          this.ball.update()

          // Update paddles
          this.paddle1.update()
          this.paddle2.update()

          // Test for collisions with paddles
          game.physics.arcade.collide(this.ball.sprite, this.paddle1.sprite, this.ballHitPaddle, null, this)
          game.physics.arcade.collide(this.ball.sprite, this.paddle2.sprite, this.ballHitPaddle, null, this)
        },

        render: () => {

        }
      }});

      
    },

    setupCallbacks() {
      this.network.callbacks['paddleMove'] = (msg) => {

        // console.log('paddleMove', msg, this.player1Id)

        if(msg.cid === this.player1.identity) {
          this.manipulatePaddle(0,msg.msg)
        }
        if(msg.cid === this.player2.identity) {
          this.manipulatePaddle(1,msg.msg)
        }
      }
    },

    setupBots() {
      this.bot1 = this.cpu1 ? new BotAI('left', this.cpu1Profile) : null
      this.bot2 = this.cpu2 ? new BotAI('right', this.cpu2Profile) : null
    },

    updateBot(playerIndex) {
      const isPlayerOne = playerIndex === 0
      const bot = isPlayerOne ? this.bot1 : this.bot2
      const paddle = isPlayerOne ? this.paddle1 : this.paddle2
      if (!bot || !paddle || !this.ball || !this.ball.sprite || !this.ball.sprite.body) {
        return
      }

      const direction = bot.update({
        ball: {
          x: this.ball.sprite.position.x,
          y: this.ball.sprite.position.y,
          vx: this.ball.sprite.body.velocity.x,
          vy: this.ball.sprite.body.velocity.y,
          radius: this.ball.sprite.width / 2,
        },
        paddle: {
          x: paddle.sprite.position.x,
          y: paddle.sprite.position.y,
        },
        bounds: {
          top: 0,
          bottom: this.game.world.height,
          centerY: this.game.world.centerY,
        },
      })

      this.manipulatePaddle(playerIndex, direction)
    },

    ballScored(side) {
      switch (side) {
        case( 'left') :
          this.sound.play('default');
          this.score[1]++
          break
        case( 'right') :
          this.sound.play('default');
          this.score[0]++
          break
        default:
          break
      }
      this.updateScore()
    },

    updateScore() {
      const scoreBoard = this.getRef('scoreboard')
      const gameContainer = this.getRef('game-container')

      applyScoreEffects({
        sound: this.sound,
        scoreBoard,
        gameContainer,
      })
      if (scoreBoard) {
        scoreBoard.innerHTML = this.score[0] + ' : ' + this.score[1]
      }

      if(this.score[0] === this.targetScore) {
        this.playerWon('left')
      }
      else if(this.score[1] === this.targetScore) {
        this.playerWon('right')
      }
      else {
        this.ball.restart()
      }
    },

    playerWon(side) {
      var playerNumber
      switch(side) {
        case('left') :
          playerNumber = 1
          break
        case('right') :
          playerNumber = 2
          break
        default:
          break
      }

      const winnerIdentity = playerNumber === 1 ? this.player1.identity : this.player2.identity

      const gameMessage = this.getRef('game-message')
      if (gameMessage) {
        gameMessage.innerHTML = 'Player ' + playerNumber + ' won!'
        gameMessage.style.visibility = 'visible'
      }

      this.onMatchFinished({
        player1Identity: this.player1.identity,
        player2Identity: this.player2.identity,
        score1: this.score[0],
        score2: this.score[1],
        winnerIdentity,
      })

      this.matchResetTimeoutId = setTimeout(function() {
        const activeGameMessage = this.getRef('game-message')
        if (activeGameMessage) {
          activeGameMessage.style.visibility = 'hidden'
        }
        this.score = [0, 0]
        this.updateScore()
        if (this.ball && typeof this.ball.restart === 'function') {
          this.ball.restart()
        }
        this.matchResetTimeoutId = null
      }.bind(this), 6000)
    },

    ballHitPaddle(ball, paddle) {
      // Send the event to the ball and paddle instances
      ball.owner.collidePaddle(paddle.owner)
      paddle.owner.collideBall(ball.owner)
      const speed = Math.abs(ball.owner.sprite.body.velocity.x)
      applyPaddleHitEffects({
        gameContainer: this.getRef('game-container'),
        side: paddle.owner.paddleId,
        speed,
      })
      applyRallyHeatEffects({
        scoreBoard: this.getRef('scoreboard'),
        speed,
      })
    },

    manipulatePaddle(playerIndex, direction) {
      switch (playerIndex) {
        case( 0) :
          this.paddle1.direction = direction
          break
        case( 1) :
          this.paddle2.direction = direction
          break
        default:
          break
      }
    },
  }

  ,

  beforeDestroy() {
    this.terminate()
  }

}
</script>

<style lang="scss">
.screen-container {
  flex: 1;
  position: relative;
}
.screen {
  @import './../../../colors.scss';

  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;

  display: flex;
  flex-direction: column;
  justify-content: center;

  transform-origin: center;

  .game-container {
    text-align: center;
    overflow: visible;
    transition: opacity 1.5s ease-in-out;
    opacity: 0;

    &.score-flash {
      box-shadow: 0 0 40px var(--theme-score-flash);
    }

    &.hit-flash-left {
      box-shadow: -14px 0 30px var(--theme-hit-flash);
    }

    &.hit-flash-right {
      box-shadow: 14px 0 30px var(--theme-hit-flash);
    }

    &.hit-flash-subtle-left {
      box-shadow: -6px 0 12px rgba(var(--theme-main-rgb), 0.28);
    }

    &.hit-flash-subtle-right {
      box-shadow: 6px 0 12px rgba(var(--theme-main-rgb), 0.28);
    }

    &.hit-flash-medium-left {
      box-shadow: -10px 0 20px rgba(var(--theme-main-rgb), 0.38);
    }

    &.hit-flash-medium-right {
      box-shadow: 10px 0 20px rgba(var(--theme-main-rgb), 0.38);
    }

    &.hit-flash-intense-left {
      box-shadow: -16px 0 34px var(--theme-hit-flash);
    }

    &.hit-flash-intense-right {
      box-shadow: 16px 0 34px var(--theme-hit-flash);
    }

    &.ready {
      opacity: 1;
    }
  }

  #phaser-container {
    canvas {
      border: 2px solid $main;
    }
  }

  .title {
    transition: all 1.5s ease-out;
    display: none;
  }

  .title-initial {
    font-size: 2.5em;
  }

  .title-in-game {
    font-size: 1.5em;
  }

  .score-board {
    display: block;
    margin: 0px;
    padding: 15px;
    font-size: 1.5em;
    // display: none;

    &.rally-heat-subtle {
      text-shadow: 0 0 8px rgba(var(--theme-main-rgb), 0.35);
    }

    &.rally-heat-medium {
      text-shadow: 0 0 12px rgba(var(--theme-main-rgb), 0.6);
    }

    &.rally-heat-high {
      text-shadow: 0 0 16px rgba(var(--theme-main-rgb), 0.88);
    }
  }

  .game-message {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    font-size: 1.5em;
  }

}

</style>
